import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';

import { useGetLatestTosVersionQuery, useLatestAcceptedVersionQuery, useAcceptTosMutation } from '@carcareers/store';
import { useAuth } from './AuthContext';

interface TOSContextProps {
  isCheckingTOS: boolean;
}

const TOSContext = createContext<TOSContextProps | null>(null);

export const useTOS = () => {
  const context = useContext(TOSContext);
  if (!context) {
    throw new Error('useTOS must be used within a TOSProvider');
  }
  return context;
};

interface TOSProviderProps {
  children: ReactNode;
}

const getBrowserInfo = (): string => {
  if (typeof window !== 'undefined') {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    return `${userAgent} - ${platform}`;
  }
  return 'Unknown Browser';
};

export const TOSProvider: React.FC<TOSProviderProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const [showTOSDialog, setShowTOSDialog] = useState(false);
  const [isCheckingTOS, setIsCheckingTOS] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [shouldCheckTOS, setShouldCheckTOS] = useState(false);

  const { data: latestTos, isLoading: isLoadingLatestTos } = useGetLatestTosVersionQuery(undefined, {
    skip: !shouldCheckTOS,
  });

  const { data: userAcceptance, isLoading: isLoadingAcceptance } = useLatestAcceptedVersionQuery(user?.email || '', {
    skip: !shouldCheckTOS,
  });

  const [acceptTos] = useAcceptTosMutation();

  useEffect(() => {
    if (!user?.email) {
      setIsCheckingTOS(false);
      setShouldCheckTOS(false);
      return;
    }

    // Check if user just logged in
    const shouldCheck = sessionStorage.getItem('checkTOS') === 'true';
    if (shouldCheck) {
      setShouldCheckTOS(true);
      setIsCheckingTOS(true);
    }
  }, [user?.email]);

  useEffect(() => {
    if (!shouldCheckTOS) {
      return;
    }

    if (isLoadingLatestTos || isLoadingAcceptance) {
      setIsCheckingTOS(true);
      return;
    }

    setIsCheckingTOS(false);

    if (latestTos) {
      const hasAcceptedLatest = userAcceptance?.version === latestTos.version;
      
      if (!hasAcceptedLatest) {
        setShowTOSDialog(true);
      } else {
        // User has already accepted latest TOS, clear the flag
        sessionStorage.removeItem('checkTOS');
      }
    }
  }, [shouldCheckTOS, latestTos, userAcceptance, isLoadingLatestTos, isLoadingAcceptance]);

  const handleAcceptTOS = async () => {
    if (!user?.email || !latestTos) return;

    setIsAccepting(true);
    try {
      await acceptTos({
        email: user.email,
        version: latestTos.version,
        timestamp: new Date().toISOString(),
        acceptedFrom: getBrowserInfo(),
      }).unwrap();

      setShowTOSDialog(false);
      // Clear the flag after successful acceptance
      sessionStorage.removeItem('checkTOS');
    } catch (error) {
      console.error('Failed to accept TOS:', error);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDeclineTOS = async () => {
    setShowTOSDialog(false);
    // Clear the flag after decline
    sessionStorage.removeItem('checkTOS');
    await signOut();
  };

  const value = {
    isCheckingTOS,
  };

  return (
    <TOSContext.Provider value={value}>
      {children}
      
      <Dialog
        open={showTOSDialog}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="h2">
            Terms of Service Agreement Required
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please review and accept our updated Terms of Service to continue using the platform.
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          {isLoadingLatestTos ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : latestTos ? (
            <>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Version {latestTos.version} - {new Date(latestTos.timestamp).toLocaleDateString()}
              </Typography>

              <Box 
                mt={2}
                dangerouslySetInnerHTML={{ __html: latestTos.content }}
                sx={{
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    marginTop: 2,
                    marginBottom: 1,
                  },
                  '& p': {
                    marginBottom: 1,
                  },
                  '& ul, & ol': {
                    marginBottom: 1,
                    paddingLeft: 2,
                  },
                  '& li': {
                    marginBottom: 0.5,
                  },
                }}
              />
            </>
          ) : (
            <Alert severity="error">Failed to load Terms of Service. Please try again later.</Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDeclineTOS} variant="outlined" color="secondary" disabled={isAccepting}>
            Decline & Sign Out
          </Button>
          <Button
            onClick={handleAcceptTOS}
            variant="contained"
            color="primary"
            disabled={isAccepting || !latestTos}
            sx={{ ml: 2 }}
          >
            {isAccepting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Accepting...
              </>
            ) : (
              'Accept & Continue'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </TOSContext.Provider>
  );
};

export default TOSContext;
